<div class="p-3">
  <!-- Toolbar -->
  <div class="d-flex justify-content-between align-items-center mb-3">
    <h5 class="mb-0 fw-bold">Client Sync Configurations</h5>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-secondary" (click)="triggerImport()">
        Import from JSON
      </button>
      <button class="btn btn-sm btn-primary" (click)="addConfig()">
        + Add Config
      </button>
    </div>
  </div>

  <!-- Hidden file input for JSON import -->
  <input type="file" id="syncImportFile" accept=".json" style="display:none"
         (change)="onFileImport($event)">

  <!-- Error banner -->
  <div *ngIf="error" class="alert alert-danger d-flex justify-content-between align-items-center py-2">
    <span>{{ error }}</span>
    <button type="button" class="btn-close" (click)="error = null"></button>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="text-center text-muted py-4">Loading...</div>

  <!-- Empty state -->
  <div *ngIf="!loading && configs.length === 0" class="text-center text-muted py-5">
    No sync configurations yet. Click <strong>+ Add Config</strong> to create one,<br>
    or <strong>Import from JSON</strong> to upload an existing one.
  </div>

  <!-- Config cards -->
  <div *ngFor="let config of configs; let ci = index; trackBy: trackByIndex"
       class="card shadow-sm mb-3">

    <!-- ===== VIEW MODE header ===== -->
    <div class="card-header d-flex justify-content-between align-items-center"
         *ngIf="!config.editing">
      <div class="d-flex align-items-center gap-2"
           style="cursor:pointer; user-select:none"
           (click)="config.viewExpanded = !config.viewExpanded">
        <span class="text-muted" style="font-size:0.75rem">{{ config.viewExpanded ? '▾' : '▸' }}</span>
        <span class="fw-bold">{{ config.name || '(Unnamed)' }}</span>
        <span class="ms-1 text-muted small" *ngIf="getDataSetName(config.dataSetId)">
          — {{ getDataSetName(config.dataSetId) }}
        </span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <span class="badge"
              [class.bg-success]="config.enabled"
              [class.bg-danger]="!config.enabled">
          {{ config.enabled ? 'Enabled' : 'Disabled' }}
        </span>
        <button class="btn btn-sm btn-outline-primary" (click)="editConfig(config)">Edit</button>
        <button class="btn btn-sm btn-outline-danger" (click)="deleteConfig(config)">Delete</button>
      </div>
    </div>

    <!-- ===== VIEW MODE body ===== -->
    <div class="card-body py-2 px-3" *ngIf="!config.editing && config.viewExpanded">
      <div *ngIf="!config.jobPayload" class="text-muted small fst-italic">No payload configured.</div>
      <ng-container *ngIf="config.jobPayload">
        <div *ngIf="getPayloadTables(config.jobPayload).length === 0"
             class="text-muted small fst-italic">No tables configured.</div>
        <div *ngFor="let table of getPayloadTables(config.jobPayload); let ti = index"
             class="mb-2" [class.mt-2]="ti > 0">
          <strong class="small">{{ table.TableName }}</strong>
          <span *ngIf="table.GroupByColumns?.length" class="text-muted small ms-2">
            | Group By: {{ table.GroupByColumns.join(', ') }}
          </span>
          <div class="small text-muted ms-1 bold" *ngIf="table.Columns?.length">
            <strong class="text-dark">Columns:</strong> {{ columnPreview(table.Columns) }}
            <span *ngIf="table.Columns.length > 6"> +{{ table.Columns.length - 6 }} more</span>
          </div>
          <div class="small text-muted ms-1 fst-italic" *ngIf="!table.Columns?.length">All columns</div>
        </div>
      </ng-container>
    </div>

    <!-- ===== EDIT MODE body ===== -->
    <div class="card-body" *ngIf="config.editing">
      <h6 class="fw-bold text-muted mb-3">Basic Settings</h6>

      <div class="row g-3 mb-3">
        <!-- Name -->
        <div class="col-md-4">
          <label class="form-label fw-bold small">
            Name <span class="text-danger">*</span>
          </label>
          <input class="form-control form-control-sm"
                 [(ngModel)]="config.name"
                 placeholder="e.g. Daily Well Sync">
        </div>

        <!-- DataSetId -->
        <div class="col-md-4">
          <label class="form-label fw-bold small">Data Set</label>
          <select class="form-select form-select-sm"
                  [(ngModel)]="config.dataSetId">
            <option value="">-- Select Data Set --</option>
            <option *ngFor="let ds of dataSets" [value]="ds.id">{{ ds.name }}</option>
          </select>
        </div>

        <!-- Enabled -->
        <div class="col-md-2">
          <label class="form-label fw-bold small">Enabled</label>
          <div class="mt-1">
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" role="switch"
                     [(ngModel)]="config.enabled"
                     [id]="'enabled_' + ci">
              <label class="form-check-label small" [for]="'enabled_' + ci">
                {{ config.enabled ? 'Yes' : 'No' }}
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== Job Payload ===== -->
      <div class="mb-3"></div>
      <h6 class="fw-bold text-muted mb-3">Job Payload (Bulk Import Options)</h6>

      <ng-container *ngIf="config.payloadUI as p">
        <div class="row g-3 mb-3">
          <!-- AllFields -->
          <div class="col-md-2">
            <div class="form-check form-switch mt-3">
              <input class="form-check-input" type="checkbox" role="switch"
                     [(ngModel)]="p.AllFields"
                     [id]="'allFields_' + ci">
              <label class="form-check-label small fw-bold" [for]="'allFields_' + ci">
                Sync All Fields
              </label>
            </div>
          </div>

          <!-- LegacySync -->
          <div class="col-md-2">
            <div class="form-check form-switch mt-3">
              <input class="form-check-input" type="checkbox" role="switch"
                     [(ngModel)]="p.LegacySync"
                     [id]="'legacySync_' + ci">
              <label class="form-check-label small fw-bold" [for]="'legacySync_' + ci">
                Use Legacy Sync
              </label>
            </div>
          </div>

          <!-- NumberThreads -->
          <div class="col-md-2">
            <label class="form-label fw-bold small"># Threads</label>
            <input type="number" class="form-control form-control-sm"
                   [(ngModel)]="p.NumberThreads"
                   min="1" max="64" step="1">
          </div>

          <!-- ModDateField -->
          <div class="col-md-3">
            <label class="form-label fw-bold small">Mod Date Field</label>
            <input class="form-control form-control-sm"
                   [(ngModel)]="p.ModDateField"
                   placeholder="e.g. ModifiedDate">
          </div>

          <!-- ClientConnectionId -->
          <div class="col-md-3">
            <label class="form-label fw-bold small">Client Connection</label>
            <select class="form-select form-select-sm"
                    [(ngModel)]="p.ClientConnectionId">
              <option value="">-- None --</option>
              <option *ngFor="let conn of connections" [value]="conn.id">{{ conn.name }}</option>
            </select>
          </div>
        </div>

        <!-- ===== Tables ===== -->
        <div class="mb-3"></div>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="fw-bold text-muted mb-0">Tables</h6>
          <button class="btn btn-sm btn-outline-secondary" (click)="addTable(config)">
            + Add Table
          </button>
        </div>

        <!-- Per-table block -->
        <div *ngFor="let table of p.Tables; let ti = index; trackBy: trackByIndex"
             class="sync-table-block border rounded mb-2">

          <!-- Collapse header -->
          <div class="d-flex align-items-center justify-content-between px-3 py-2 bg-light rounded-top"
               style="cursor:pointer; user-select:none"
               (click)="table.expanded = !table.expanded">
            <div class="d-flex align-items-center gap-2">
              <span class="small fw-bold" style="font-size:0.8rem">
                {{ table.expanded ? '▾' : '▸' }}
              </span>
              <span class="small fw-semibold">
                {{ table.TableName || 'New Table' }}
              </span>
              <span *ngIf="table.Schema" class="badge bg-secondary fw-normal small">{{ table.Schema }}</span>
              <span *ngIf="table.GroupByColumns.length" class="text-muted small">
                · Group by: {{ table.GroupByColumns.join(', ') }}
              </span>
              <span *ngIf="table.Columns.length" class="text-muted small">
                · {{ table.Columns.length }} column{{ table.Columns.length === 1 ? '' : 's' }}
              </span>
            </div>
            <button class="btn btn-sm btn-outline-danger py-0 px-2"
                    style="font-size:0.75rem"
                    (click)="$event.stopPropagation(); removeTable(config, ti)">
              Remove
            </button>
          </div>

          <!-- Expandable body -->
          <div *ngIf="table.expanded" class="p-3">

          <!-- Table selector -->
          <div class="d-flex align-items-end gap-2 mb-3">
            <div class="flex-grow-1" style="max-width: 360px">
              <label class="form-label fw-bold small mb-1">Table</label>

              <!-- Backdrop: closes dropdown when clicking outside -->
              <div *ngIf="table.tableDropdownOpen"
                   class="position-fixed top-0 start-0 w-100 h-100"
                   style="z-index:999"
                   (click)="table.tableDropdownOpen = false; table.tableSearch = ''; filterTableOptions(table, '')">
              </div>

              <div class="position-relative">
                <!-- Trigger -->
                <button type="button"
                        class="form-select form-select-sm text-start text-truncate"
                        (click)="openDropdown(table, $event)">
                  {{ table.TableName || '-- Select Table --' }}
                </button>

                <!-- Dropdown panel — position:fixed so it renders above all stacked elements -->
                <div *ngIf="table.tableDropdownOpen"
                     class="position-fixed bg-white border rounded shadow-sm"
                     [style.top.px]="table.dropdownTop"
                     [style.left.px]="table.dropdownLeft"
                     [style.width.px]="table.dropdownWidth"
                     style="z-index:9999">
                  <!-- Search -->
                  <div class="p-1 border-bottom">
                    <input class="form-control form-control-sm"
                           [(ngModel)]="table.tableSearch"
                           (ngModelChange)="filterTableOptions(table, $event)"
                           placeholder="Search tables..."
                           type="text"
                           (click)="$event.stopPropagation()"
                           autofocus>
                  </div>
                  <!-- Options -->
                  <div style="max-height:220px; overflow-y:auto">
                    <div *ngFor="let t of table.filteredTableOptions"
                         class="px-3 py-1 small table-option-item"
                         (click)="selectTable(config, table, t)">
                      {{ t.tableName }}
                    </div>
                    <div *ngIf="table.filteredTableOptions.length === 0"
                         class="px-3 py-2 small text-muted">
                      No tables found.
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- Table model info (visible when a table is selected) -->
          <div class="mb-3" *ngIf="table.tableKey">
            <label class="form-label fw-bold small mb-1">Table Details</label>
            <div class="border rounded p-2 bg-light">
              <div class="row g-2 align-items-end">
                <div class="col-md-2">
                  <label class="form-label small mb-1 text-muted">Schema</label>
                  <input class="form-control form-control-sm" [value]="table.Schema" disabled>
                </div>
                <div class="col-md-3">
                  <label class="form-label small mb-1 text-muted">Table Name</label>
                  <input class="form-control form-control-sm" [value]="table.TableName" disabled>
                </div>
                <div class="col-md-3">
                  <label class="form-label small mb-1 text-muted">Display Name</label>
                  <input class="form-control form-control-sm"
                         [(ngModel)]="table.displayName"
                         placeholder="Display name...">
                </div>
                <div class="col-md-1">
                  <label class="form-label small mb-1 text-muted">Major Ver.</label>
                  <input type="number" class="form-control form-control-sm"
                         [(ngModel)]="table.fromMajorVersion"
                         min="0" step="1">
                </div>
                <div class="col-md-1">
                  <label class="form-label small mb-1 text-muted">Minor Ver.</label>
                  <input type="number" class="form-control form-control-sm"
                         [(ngModel)]="table.fromMinorVersion"
                         min="0" step="1">
                </div>
                <div class="col-md-2 d-flex align-items-end">
                  <button class="btn btn-sm btn-outline-primary w-100"
                          (click)="saveTableModel(table)"
                          [disabled]="table.savingTableInfo || !table.tableId">
                    <span *ngIf="table.savingTableInfo"
                          class="spinner-border spinner-border-sm me-1"
                          role="status"></span>
                    {{ table.savingTableInfo ? 'Saving...' : 'Save Info' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Field list -->
          <div class="mb-3" *ngIf="table.tableKey">
            <label class="form-label fw-bold small mb-1">Fields</label>
            <div *ngIf="table.loadingFields" class="text-muted small">Loading fields...</div>
            <div *ngIf="!table.loadingFields && table.tableFields.length === 0 && table.tableId"
                 class="text-muted small fst-italic">No fields found.</div>
            <div *ngIf="!table.loadingFields && table.tableFields.length === 0 && !table.tableId"
                 class="text-muted small fst-italic">Table not registered in data model — field details unavailable.</div>
            <div *ngIf="table.tableFields.length > 0"
                 class="border rounded" style="max-height:260px; overflow-y:auto">
              <table class="table table-sm table-hover mb-0 small">
                <thead class="table-light sticky-top">
                  <tr>
                    <th>Field Name</th>
                    <th>Display Name</th>
                    <th>Type</th>
                    <th class="text-center">Nullable</th>
                    <th class="text-center">PK</th>
                    <th class="text-center">Unique</th>
                    <th class="text-center">Key Order</th>
                    <th class="text-center">Char Limit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let f of table.tableFields">
                    <td class="fw-semibold">{{ f.fieldName }}</td>
                    <td class="text-muted">{{ f.displayName }}</td>
                    <td>{{ f.fieldTypeString }}</td>
                    <td class="text-center">{{ f.isNullable ? '✓' : '' }}</td>
                    <td class="text-center">{{ f.isPrimaryKey ? '✓' : '' }}</td>
                    <td class="text-center">{{ f.isUniqueKeyPart ? '✓' : '' }}</td>
                    <td class="text-center">{{ f.keyOrder ?? '' }}</td>
                    <td class="text-center">{{ f.charLimit ?? '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Group By Columns -->
          <div class="mb-3" *ngIf="table.tableKey">
            <label class="form-label fw-bold small mb-1">Group By Columns</label>
            <div class="d-flex align-items-center gap-2 mb-2">
              <select class="form-select form-select-sm"
                      [(ngModel)]="table.pendingGroupByColumn"
                      style="max-width: 300px">
                <option value="">-- Select column --</option>
                <option *ngFor="let c of getColumnOptionsForTable(table)" [value]="c">{{ c }}</option>
              </select>
              <button class="btn btn-sm btn-outline-secondary"
                      (click)="addGroupBy(table)"
                      [disabled]="!table.pendingGroupByColumn">
                + Add
              </button>
            </div>
            <div class="d-flex flex-wrap gap-1">
              <span *ngFor="let col of table.GroupByColumns; let gi = index"
                    class="badge bg-secondary d-flex align-items-center gap-1">
                {{ col }}
                <button type="button" class="btn-close btn-close-white"
                        style="font-size: 0.55rem"
                        (click)="removeGroupBy(table, gi)">
                </button>
              </span>
            </div>
          </div>

          <!-- Columns -->
          <div *ngIf="table.tableKey">
            <label class="form-label fw-bold small mb-1">Columns to Sync</label>
            <div class="d-flex justify-content-end mb-2">
              <button class="btn btn-sm btn-outline-secondary" (click)="addColumn(table)">
                + Add Column
              </button>
            </div>

            <div *ngIf="table.Columns.length === 0" class="text-muted small">
              No columns configured — all columns will be synced if "Sync All Fields" is on.
            </div>

            <!-- Column grid header -->
            <div *ngIf="table.Columns.length > 0" class="d-flex gap-2 mb-1 px-1">
              <span class="small text-muted fw-bold" style="width:130px">Aggregation</span>
              <span class="small text-muted fw-bold" style="width:200px">Column</span>
              <span class="small text-muted fw-bold" style="width:160px">Alias (optional)</span>
            </div>

            <!-- Column rows -->
            <div *ngFor="let col of table.Columns; let colI = index; trackBy: trackByIndex"
                 class="d-flex align-items-center gap-2 mb-1">
              <select class="form-select form-select-sm" style="width:130px"
                      [(ngModel)]="col.AggregationType">
                <option *ngFor="let agg of aggTypes" [value]="agg">{{ agg }}</option>
              </select>

              <select class="form-select form-select-sm" style="width:200px"
                      [(ngModel)]="col.ColumnName">
                <option value="">-- Column --</option>
                <option *ngFor="let c of getColumnOptionsForTable(table)" [value]="c">{{ c }}</option>
              </select>

              <input class="form-control form-control-sm" style="width:160px"
                     [(ngModel)]="col.ColumnMapping"
                     placeholder="Alias (optional)">

              <button class="btn btn-sm btn-outline-danger px-2"
                      (click)="removeColumn(table, colI)"
                      title="Remove column">×</button>
            </div>
          </div>
          </div><!-- /expandable body -->
        </div><!-- /sync-table-block -->

        <div *ngIf="p.Tables.length === 0" class="text-muted small mb-2">
          No tables configured. Click "+ Add Table" to add one.
        </div>
      </ng-container>

      <!-- Save / Cancel -->
      <div class="d-flex gap-2 mt-4">
        <button class="btn btn-sm btn-success" (click)="saveConfig(config)">Save</button>
        <button class="btn btn-sm btn-outline-secondary" (click)="cancelEdit(config)">Cancel</button>
      </div>
    </div>
  </div>
</div>
